import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgStyle } from '@angular/common';
import { TranslocoDirective, translateSignal } from '@jsverse/transloco';
import { SoloTierData } from 'src/app/core/api/player/schemas/solo-tiers';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { ButtonComponent } from '../../../../ui/buttons/button/button.component';
import { CurrencyAmountComponent } from '../../../../ui/currency/currency-amount/currency-amount.component';
import { IconComponent } from '../../../../ui/icon/icon/icon.component';
import { SplicedBackgroundComponent } from '../../../../ui/surface/spliced-background/spliced-background.component';
import { SoloStatComponent } from '../solo-stat/solo-stat.component';
import { MULTIPLIER_MARKER, splitAtMultiplier } from '../../utils/multiplier-sentence.utils';
import {
  SoloAmountDisplay,
  soloAmountDisplay,
  sortSoloTiersByEntryFee,
} from '../../utils/solo-tiers.utils';

// The Solo entry point at the top of the tier grid: the target to beat, the best prize on offer,
// the cheapest entry and the multiple of the entry a win pays. A tap anywhere opens the Solo
// sheet, where the stake is chosen, so the card raises no payment itself. The tier list renders it
// only while wager offers Solo tiers.
@Component({
  selector: 'app-solo-card',
  templateUrl: './solo-card.component.html',
  styleUrls: ['./solo-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    TranslocoDirective,
    ThemePipe,
    ButtonComponent,
    CurrencyAmountComponent,
    IconComponent,
    SoloStatComponent,
    SplicedBackgroundComponent,
  ],
})
export class SoloCardComponent {
  // Never empty: an empty list means Solo is off, and the tier list then leaves the card out.
  readonly soloTiers = input.required<SoloTierData[]>();
  // Must match the tier tiles' payment-in-flight guard, so the whole grid goes inert together.
  readonly disabled = input.required<boolean>();

  readonly clickEvent = output<void>();

  private readonly themeService = inject(ThemeService);

  private readonly tiersByEntryFee = computed(() => sortSoloTiersByEntryFee(this.soloTiers()));

  // Every tier carries the same target, so any tier's will do.
  readonly target = computed(() =>
    this.themeService.formatNumber(this.tiersByEntryFee()[0].targetScore),
  );

  // Every tier pays the same multiple, so any tier's will do.
  readonly multiplier = computed(() =>
    this.themeService.formatNumber(Number(this.tiersByEntryFee()[0].multiplier)),
  );

  // A signal, so the call to action follows a language switch as the template's other text does.
  private readonly ctaSentence = translateSignal('components.solo.win-multiplier', {
    multiplier: MULTIPLIER_MARKER,
  });

  // The call to action, cut around the multiplier so the template colours "7x" alone.
  readonly cta = computed(() => splitAtMultiplier(this.ctaSentence(), this.multiplier()));

  readonly maxPrize = computed(() => this.format(this.tiersByEntryFee().at(-1)!, 'prize'));
  readonly minEntry = computed(() => this.format(this.tiersByEntryFee()[0], 'entryFee'));

  // The paid tier tiles' frame (Pixel Pioneers' sliced card image), so Solo sits in the grid
  // framed like the tiles below it, as its face, outline and radius already follow theirs. Null
  // on skins that draw no frame.
  readonly splicedBg = this.themeService.splicedBackgroundPlacement('wager-card');

  readonly cardStyles: [string, string][] = [
    ['background', 'card-solo'],
    ['border', 'card-solo'],
    ['border-radius', 'card-solo'],
  ];
  readonly beatStyles: [string, string][] = [
    ['type', 'card-solo-label-beat'],
    ['text-color', 'card-solo-label-beat'],
  ];
  // The box figures carry the paid card's prize outline (text-outer-border), so they match the duel
  // prize on the skins that draw one; must match that prize's styles in wager-card.
  readonly targetStyles: [string, string][] = [
    ['type', 'card-solo-label-target'],
    ['text-outer-border', 'card-solo-label-target'],
    ['text-color', 'card-solo-label-target'],
  ];
  readonly unitStyles: [string, string][] = [
    ['type', 'card-solo-label-unit'],
    ['text-color', 'card-solo-label-unit'],
  ];
  readonly statValueStyles: [string, string][] = [
    ['type', 'card-solo-stat-value'],
    ['text-outer-border', 'card-solo-stat-value'],
    ['text-color', 'card-solo-stat-value'],
  ];
  readonly statPrizeStyles: [string, string][] = [
    ['type', 'card-solo-stat-prize'],
    ['text-outer-border', 'card-solo-stat-prize'],
    ['text-color', 'card-solo-stat-prize'],
  ];

  readonly ctaStyles: [string, string][] = [
    ['type', 'card-solo-label-cta'],
    ['text-color', 'card-solo-label-cta'],
  ];
  readonly ctaMultiplierStyles: [string, string][] = [
    ['text-color', 'card-solo-label-cta-multiplier'],
  ];

  onClick(): void {
    if (this.disabled()) {
      return;
    }
    this.clickEvent.emit();
  }

  private format(tier: SoloTierData, field: 'entryFee' | 'prize'): SoloAmountDisplay {
    return soloAmountDisplay(
      tier.amounts,
      field,
      this.themeService.displayCurrency(),
      (currency, amount) => this.themeService.formatCurrencyAmount(currency, amount),
    );
  }
}
